import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Switch } from '../../ui/switch';
import { api } from '../../../context/AuthContext';
import { cn } from '../../../lib/utils';
import { Plus, TrendingUp, Target } from 'lucide-react';
import { toast } from 'sonner';
import { INSTRUMENT_TEMPLATES, EMPTY_INSTRUMENT_FORM, toFormData } from './instrumentTemplates';

/**
 * Single canonical create/edit form for instruments (instrument === offering).
 * Used by the Instruments page and the Project > Instruments tab.
 */
export const InstrumentFormDialog = ({
  open,
  onOpenChange,
  instrument = null,
  projects = [],
  lockedProjectId = null,
  onSaved,
}) => {
  const isEditing = Boolean(instrument?.id);
  const [step, setStep] = useState(isEditing ? 'form' : 'template');
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY_INSTRUMENT_FORM);

  useEffect(() => {
    if (!open) return;
    if (instrument?.id) {
      setFormData(toFormData(instrument));
      setStep('form');
    } else {
      setFormData({ ...EMPTY_INSTRUMENT_FORM, project_id: lockedProjectId || '' });
      setStep('template');
    }
  }, [open, instrument, lockedProjectId]);

  const update = (patch) => setFormData((prev) => ({ ...prev, ...patch }));

  const applyTemplate = (template) => {
    setFormData({ ...EMPTY_INSTRUMENT_FORM, project_id: lockedProjectId || '', ...(template?.defaults || {}) });
    setStep('form');
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Instrument name is required');
      return;
    }
    if (!formData.total_authorized_units || parseInt(formData.total_authorized_units) <= 0) {
      toast.error('Total authorized units must be greater than 0');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        total_authorized_units: parseInt(formData.total_authorized_units),
        project_id: lockedProjectId || formData.project_id || null,
        min_subscription_units: formData.min_subscription_units ? parseInt(formData.min_subscription_units) : 1,
        max_subscription_units: formData.max_subscription_units ? parseInt(formData.max_subscription_units) : null,
        subscription_start: formData.subscription_start || null,
        subscription_end: formData.subscription_end || null,
        soft_cap: formData.soft_cap || null,
        hard_cap: formData.hard_cap || null,
        min_hold_period_months: formData.min_hold_period_months ? parseInt(formData.min_hold_period_months) : null,
        max_hold_period_months: formData.max_hold_period_months ? parseInt(formData.max_hold_period_months) : null,
        target_hold_months: formData.target_hold_months ? parseInt(formData.target_hold_months) : null,
        estimated_monthly_noi_pct: formData.estimated_monthly_noi_pct || null,
        estimated_total_appreciation_pct: formData.estimated_total_appreciation_pct || null,
        fee_percent: formData.fee_percent || '2.5',
        project_start_date: formData.project_start_date || null,
        project_exit_date: formData.project_exit_date || null,
      };

      if (isEditing) {
        await api.put(`/admin/projects/instruments/${instrument.id}`, payload);
        toast.success('Instrument updated');
      } else {
        await api.post('/admin/projects/instruments', payload);
        toast.success('Instrument created');
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save instrument');
    } finally {
      setSaving(false);
    }
  };

  const totalRaiseTarget =
    (parseFloat(formData.total_authorized_units || 0) || 0) * (parseFloat(formData.unit_price_at_issuance || 0) || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto" data-testid="instrument-form-dialog">
        {step === 'template' ? (
          <>
            <DialogHeader>
              <DialogTitle>Choose Instrument Template</DialogTitle>
              <DialogDescription>
                Select a template to pre-fill common configurations, or start from scratch.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-4 mt-4">
              {INSTRUMENT_TEMPLATES.map((template) => {
                const Icon = template.icon;
                return (
                  <button
                    key={template.id}
                    onClick={() => applyTemplate(template)}
                    className="p-4 border border-black/[0.06] rounded-xl text-left hover:border-primary/50 hover:bg-slate-50 transition-colors group"
                    data-testid={`template-${template.id}`}
                  >
                    <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center mb-3', template.color)}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <h4 className="font-medium mb-1 group-hover:text-primary">{template.name}</h4>
                    <p className="text-xs text-foreground-muted">{template.description}</p>
                  </button>
                );
              })}
            </div>

            <div className="mt-4 pt-4 border-t">
              <Button variant="outline" className="w-full" onClick={() => applyTemplate(null)} data-testid="template-blank">
                <Plus className="h-4 w-4 mr-2" />
                Start from Scratch
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{isEditing ? 'Edit Instrument' : 'Create New Instrument'}</DialogTitle>
              <DialogDescription>
                The instrument is the investment offering: units, pricing and subscription terms live here.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div>
                <Label>Instrument Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => update({ name: e.target.value })}
                  placeholder="e.g., Lisbon Apartment A - Common Units"
                  className="mt-1"
                  data-testid="instrument-name-input"
                />
              </div>

              {!lockedProjectId && (
                <div>
                  <Label>Link to Project (optional)</Label>
                  <Select
                    value={formData.project_id || 'none'}
                    onValueChange={(v) => update({ project_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger className="mt-1" data-testid="instrument-project-select">
                      <SelectValue placeholder="Select project..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Project (Standalone)</SelectItem>
                      {projects.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.title || p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Security Class</Label>
                  <Select value={formData.security_class} onValueChange={(v) => update({ security_class: v })}>
                    <SelectTrigger className="mt-1" data-testid="instrument-class-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="common">Common</SelectItem>
                      <SelectItem value="preferred">Preferred</SelectItem>
                      <SelectItem value="convertible">Convertible</SelectItem>
                      <SelectItem value="debt">Debt</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Currency</Label>
                  <Select value={formData.currency} onValueChange={(v) => update({ currency: v })}>
                    <SelectTrigger className="mt-1" data-testid="instrument-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Total Authorized Units *</Label>
                  <Input
                    type="number"
                    value={formData.total_authorized_units}
                    onChange={(e) => update({ total_authorized_units: e.target.value })}
                    placeholder="e.g., 100000"
                    className="mt-1"
                    data-testid="instrument-units-input"
                  />
                </div>
                <div>
                  <Label>Unit Price</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.unit_price_at_issuance}
                    onChange={(e) => update({ unit_price_at_issuance: e.target.value })}
                    placeholder="1"
                    className="mt-1"
                    data-testid="instrument-price-input"
                  />
                </div>
              </div>

              {totalRaiseTarget > 0 && (
                <div className="bg-slate-50 p-3 rounded-lg flex items-center justify-between">
                  <span className="text-sm text-foreground-muted">Total Raise Target</span>
                  <span className="font-semibold" data-testid="instrument-raise-target">
                    {formData.currency} {totalRaiseTarget.toLocaleString()}
                  </span>
                </div>
              )}

              {/* ============ OFFERING / SUBSCRIPTION TERMS ============ */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Offering & Subscription Terms
                </h4>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label>Offering Status</Label>
                    <Select value={formData.offering_status} onValueChange={(v) => update({ offering_status: v })}>
                      <SelectTrigger className="mt-1" data-testid="instrument-offering-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft (not accepting)</SelectItem>
                        <SelectItem value="open">Open (accepting subscriptions)</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Min Subscription Units</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.min_subscription_units}
                      onChange={(e) => update({ min_subscription_units: e.target.value })}
                      className="mt-1"
                      data-testid="instrument-min-subscription-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label>Max Subscription Units</Label>
                    <Input
                      type="number"
                      value={formData.max_subscription_units}
                      onChange={(e) => update({ max_subscription_units: e.target.value })}
                      placeholder="No limit"
                      className="mt-1"
                      data-testid="instrument-max-subscription-input"
                    />
                  </div>
                  <div>
                    <Label>Platform Fee on Amount Raised (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.fee_percent}
                      onChange={(e) => update({ fee_percent: e.target.value })}
                      placeholder="2.5"
                      className="mt-1"
                      data-testid="instrument-fee-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label>Subscription Opens</Label>
                    <Input
                      type="date"
                      value={formData.subscription_start}
                      onChange={(e) => update({ subscription_start: e.target.value })}
                      className="mt-1"
                      data-testid="instrument-subscription-start-input"
                    />
                  </div>
                  <div>
                    <Label>Subscription Closes</Label>
                    <Input
                      type="date"
                      value={formData.subscription_end}
                      onChange={(e) => update({ subscription_end: e.target.value })}
                      className="mt-1"
                      data-testid="instrument-subscription-end-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Soft Cap ({formData.currency})</Label>
                    <Input
                      type="number"
                      value={formData.soft_cap}
                      onChange={(e) => update({ soft_cap: e.target.value })}
                      placeholder="Minimum to proceed"
                      className="mt-1"
                      data-testid="instrument-soft-cap-input"
                    />
                  </div>
                  <div>
                    <Label>Hard Cap ({formData.currency})</Label>
                    <Input
                      type="number"
                      value={formData.hard_cap}
                      onChange={(e) => update({ hard_cap: e.target.value })}
                      placeholder="Maximum to accept"
                      className="mt-1"
                      data-testid="instrument-hard-cap-input"
                    />
                  </div>
                </div>
              </div>

              {/* ============ RIGHTS ============ */}
              <div className="border-t pt-4 mt-4 space-y-4">
                <div>
                  <Label>Distribution Rights</Label>
                  <Select value={formData.distribution_rights} onValueChange={(v) => update({ distribution_rights: v })}>
                    <SelectTrigger className="mt-1" data-testid="instrument-distribution-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pro_rata">Pro Rata (equal share)</SelectItem>
                      <SelectItem value="fixed">Fixed Amount</SelectItem>
                      <SelectItem value="none">None</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.voting_rights}
                      onCheckedChange={(v) => update({ voting_rights: v })}
                      data-testid="instrument-voting-switch"
                    />
                    <Label>Voting Rights</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.transferable}
                      onCheckedChange={(v) => update({ transferable: v })}
                      data-testid="instrument-transferable-switch"
                    />
                    <Label>Transferable</Label>
                  </div>
                </div>

                <div>
                  <Label>Transfer Restrictions (if any)</Label>
                  <Textarea
                    value={formData.transfer_restrictions}
                    onChange={(e) => update({ transfer_restrictions: e.target.value })}
                    rows={2}
                    placeholder="e.g., Lock-up period of 12 months..."
                    className="mt-1"
                    data-testid="instrument-restrictions-input"
                  />
                </div>

                <div>
                  <Label>Redemption Terms</Label>
                  <Textarea
                    value={formData.redemption_terms}
                    onChange={(e) => update({ redemption_terms: e.target.value })}
                    rows={2}
                    placeholder="e.g., Redeemable at NAV after 24 months..."
                    className="mt-1"
                    data-testid="instrument-redemption-input"
                  />
                </div>
              </div>

              {/* ============ FINANCIAL TERMS ============ */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Financial Terms & Returns
                </h4>

                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <Label>Monthly rental / NOI (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.estimated_monthly_noi_pct}
                      onChange={(e) => update({ estimated_monthly_noi_pct: e.target.value })}
                      placeholder="e.g., 0.45"
                      className="mt-1"
                      data-testid="instrument-monthly-noi-input"
                    />
                    <p className="text-[11px] text-foreground-muted mt-1">Per month, as a % of invested capital</p>
                  </div>
                  <div>
                    <Label>Appreciation over lifetime (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.estimated_total_appreciation_pct}
                      onChange={(e) => update({ estimated_total_appreciation_pct: e.target.value })}
                      placeholder="e.g., 18"
                      className="mt-1"
                      data-testid="instrument-appreciation-input"
                    />
                    <p className="text-[11px] text-foreground-muted mt-1">Total project return by exit</p>
                  </div>
                  <div>
                    <Label>Target hold period (months)</Label>
                    <Input
                      type="number"
                      value={formData.target_hold_months}
                      onChange={(e) => update({ target_hold_months: e.target.value })}
                      placeholder="e.g., 12"
                      className="mt-1"
                      data-testid="instrument-target-hold-input"
                    />
                  </div>
                </div>

                {(formData.estimated_monthly_noi_pct || formData.estimated_total_appreciation_pct) && (
                  <div className="bg-emerald-50 p-3 rounded-lg mb-4 flex items-center justify-between" data-testid="instrument-irr-preview">
                    <span className="text-sm text-foreground-muted">
                      Annualized IRR = (monthly NOI × 12) + (appreciation ÷ hold × 12)
                    </span>
                    <span className="text-lg font-bold text-emerald-700">
                      {(() => {
                        const noi = parseFloat(formData.estimated_monthly_noi_pct || 0);
                        const appreciation = parseFloat(formData.estimated_total_appreciation_pct || 0);
                        const hold = parseFloat(formData.target_hold_months || 0);
                        const irr = noi * 12 + (hold ? (appreciation / hold) * 12 : appreciation);
                        return `${irr.toFixed(1)}%`;
                      })()}
                    </span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label>Project Start Date</Label>
                    <Input
                      type="date"
                      value={formData.project_start_date}
                      onChange={(e) => update({ project_start_date: e.target.value })}
                      className="mt-1"
                      data-testid="instrument-start-date-input"
                    />
                  </div>
                  <div>
                    <Label>Expected Exit Date</Label>
                    <Input
                      type="date"
                      value={formData.project_exit_date}
                      onChange={(e) => update({ project_exit_date: e.target.value })}
                      className="mt-1"
                      data-testid="instrument-exit-date-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label>Minimum Hold Period (months)</Label>
                    <Input
                      type="number"
                      value={formData.min_hold_period_months}
                      onChange={(e) => update({ min_hold_period_months: e.target.value })}
                      placeholder="e.g., 12"
                      className="mt-1"
                      data-testid="instrument-min-hold-input"
                    />
                  </div>
                  <div>
                    <Label>Target Hold Period (months)</Label>
                    <Input
                      type="number"
                      value={formData.max_hold_period_months}
                      onChange={(e) => update({ max_hold_period_months: e.target.value })}
                      placeholder="e.g., 36"
                      className="mt-1"
                      data-testid="instrument-max-hold-input"
                    />
                  </div>
                </div>

                <div>
                  <Label>Payout Terms</Label>
                  <Textarea
                    value={formData.payout_terms}
                    onChange={(e) => update({ payout_terms: e.target.value })}
                    rows={2}
                    placeholder="e.g., Quarterly distributions, paid within 30 days of quarter end..."
                    className="mt-1"
                    data-testid="instrument-payout-input"
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-instrument-btn">
                {saving ? 'Saving...' : isEditing ? 'Update Instrument' : 'Create Instrument'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InstrumentFormDialog;
